import React, { createContext, useEffect } from 'react';

// project imports
import Loader from 'ui-component/Loader';
import { useDispatch, useSelector } from 'store';
import {
  login as loginAction,
  register as registerAction,
  logout as logoutAction,
  initializeAuth,
  updateProfile as updateProfileAction,
  resetPassword as resetPasswordAction,
  confirmResetPassword as confirmResetPasswordAction,
  changePassword as changePasswordAction,
  verifyInvite as verifyInviteAction,
  acceptInvite as acceptInviteAction,
  selectIsAuthenticated,
  selectIsInitialized,
  selectAuthUser,
} from 'store/slices/auth';

// types
import { JWTContextType } from 'types/auth';

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext<JWTContextType | null>(null);

export function JWTProvider({ children }: { children: React.ReactElement }) {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isInitialized = useSelector(selectIsInitialized);
  const user = useSelector(selectAuthUser);

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  const login = async (email: string, password: string, turnstileToken?: string) => {
    await dispatch(loginAction({ email, password, turnstileToken })).unwrap();
  };

  const register = async (email: string, password: string, firstName: string, lastName: string, turnstileToken: string) => {
    await dispatch(registerAction({ email, password, firstName, lastName, turnstileToken })).unwrap();
  };

  const logout = () => {
    dispatch(logoutAction());
  };

  const resetPassword = async (email: string, turnstileToken: string) => {
    await dispatch(resetPasswordAction({ email, turnstileToken })).unwrap();
  };

  const confirmResetPassword = async (token: string, newPassword: string) => {
    await dispatch(confirmResetPasswordAction({ token, newPassword })).unwrap();
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    await dispatch(changePasswordAction({ currentPassword, newPassword })).unwrap();
  };

  const updateProfile = async () => {
    await dispatch(updateProfileAction()).unwrap();
  };

  const verifyInvite = async (token: string) => {
    return await dispatch(verifyInviteAction(token)).unwrap();
  };

  const acceptInvite = async (token: string, password: string) => {
    await dispatch(acceptInviteAction({ token, password })).unwrap();
  };

  if (!isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        isLoggedIn: isAuthenticated,
        isInitialized,
        user,
        login,
        logout,
        register,
        resetPassword,
        confirmResetPassword,
        changePassword,
        updateProfile,
        verifyInvite,
        acceptInvite,
      }}
    >
      {children}
    </JWTContext.Provider>
  );
}

export default JWTContext;
